import { useEffect, useSyncExternalStore } from 'react';
import type { CalendarItem } from './calendar-item';
import { readShowsBookingOutcome, type AwaitedBookingOutcome } from './calendar-bookings';

/**
 * How long an action's buttons wait for a read that shows it. Past this they follow the read again, so a booking someone
 * changed back meanwhile is not left without buttons for the rest of the session.
 */
const AWAIT_MS = 2 * 60_000;

// Shared by every card that draws a booking's buttons, since the details card closes once it acts and the next one to
// open for the same booking must still wait.
const awaited = new Map<string, AwaitedBookingOutcome & { at: number }>();
const listeners = new Set<() => void>();
const keyOf = (siteId: string, record: string) => `${siteId}:${record}`;
const changed = () => listeners.forEach((listener) => listener());

/** The action on this booking's client succeeded (or was undone): its buttons wait for a read that shows it. */
export function awaitBookingOutcome(siteId: string, record: string, outcome: AwaitedBookingOutcome, now: number = Date.now()): void {
  awaited.set(keyOf(siteId, record), { ...outcome, at: now });
  changed();
}

/** For tests: forget every awaited outcome. */
export function forgetAwaitedBookingOutcomes(): void {
  awaited.clear();
  changed();
}

/**
 * The outcome this booking's client still waits on, or nothing once `item` (the booking as read now) shows it or the wait
 * has run out.
 */
export function useAwaitedBookingOutcome(item: CalendarItem | undefined, siteId: string | undefined, record: string | undefined, now: number): AwaitedBookingOutcome | undefined {
  const key = siteId && record ? keyOf(siteId, record) : undefined;
  const outcome = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => (key ? awaited.get(key) : undefined),
    () => undefined,
  );
  const settled = !!outcome && (now - outcome.at > AWAIT_MS || (!!item && !!record && readShowsBookingOutcome(item, record, outcome)));
  useEffect(() => {
    if (key && settled && awaited.get(key) === outcome) {
      awaited.delete(key);
      changed();
    }
  }, [key, settled, outcome]);
  return outcome && !settled ? outcome : undefined;
}
